/*
  cvc-recipe-live.js

  Keeps the recipes on this site in step with Recipes Generator without waiting
  for the nightly sync.

  THE PROBLEM THIS SOLVES
  -----------------------
  Tammy writes her recipes in Recipes Generator. A scheduled job copies them
  into Shopify, so between her pressing save and that job running, the site is
  behind. On the old Weebly site there was no gap, because the page was drawn
  by the vendor's own widget straight from their data.

  This closes the gap the same way the widget did - by reading the vendor's
  storage from the visitor's browser - but draws the result with OUR markup, so
  nothing about the design changes. Concretely:

    index page   - a recipe Tammy has just added appears in the grid, and one
                   she has deleted disappears.
    recipe page  - an edited recipe is redrawn with the new text.
    unknown URL  - /pages/recipes/<handle> for a recipe the sync has not
                   imported yet renders the recipe instead of a 404.

  WHY IT IS SAFE TO DRAW OVER THE SERVER'S WORK
  ---------------------------------------------
  Because the drawing is not a second opinion. cvc-recipe-transform.js is a
  line-by-line port of scripts/transform.py and cvc-recipe-render.js is one of
  sections/main-recipe.liquid, and both are held to that by checks that run
  inside scripts/sync.py:

    node scripts/parity_check.js          709/709 recipes transform identically
    node scripts/render_parity_check.js   708/708 pages render identically

  If either ever fails, the sync fails, and whoever changed one of the two
  copies finds out immediately.

  WHAT IT WILL NOT DO
  -------------------
  - It never blanks or breaks a page. Every path is wrapped so that a failure
    leaves the server's own render exactly as it was.
  - It does nothing at all when the visitor has Data Saver on or is on a 2G
    connection.
  - It does not touch the parts of a page the server owns and it cannot see -
    the <title> and social tags are rewritten only for fields it has.
  - It cannot re-host images. A recipe the sync has not seen yet is shown with
    the vendor's image proxy rather than a Shopify CDN copy; the photograph is
    the same one, it is just not served from our CDN until the sync runs.
*/
(function () {
  'use strict';

  /*
    LOAD ONCE, WHATEVER THE PAGE DOES.

    This snippet is rendered by the recipe index, the recipe template, the 404
    page and the footer, so a page can easily pull it in three times. Liquid
    cannot prevent that - {% render %} has an isolated scope, so a flag set
    inside the snippet is invisible to the next render of it - and running the
    file again is not harmless: it replaced window.CVCLive with a fresh copy
    whose caches and in-flight promises were empty, orphaning whatever the
    first copy was already doing. That is what left the recipe index sitting on
    "Loading recipes..." for ever while the mega menu beside it had already
    finished. The guard belongs here, where it cannot be got wrong.
  */
  if (window.CVCLive) return;

  var T = window.CVCTransform;
  var R = window.CVCRecipeRender;
  if (!T || !R) return;              // a missing dependency must not break the page

  var cfg = window.CVCLiveConfig || {};
  var BUCKET = cfg.bucket || 'recipes-generator.appspot.com';
  var ACCOUNT = cfg.account || '';
  var BASE = 'https://firebasestorage.googleapis.com/v0/b/' + BUCKET + '/o';
  // The vendor's own image proxy. The imgur URL stored on a card serves a
  // placeholder rather than the photograph (see scripts/rehost_images.py);
  // this is where the real one comes from.
  var IMAGE_PROXY = 'https://www.recipesgenerator.com/api/get-image?id=';
  var TIMEOUT = cfg.timeoutMs || 8000;

  if (!ACCOUNT) return;

  // ---- politeness ---------------------------------------------------------

  // Nothing here is worth a penny of someone's data allowance. On Data Saver
  // or a slow connection the page stays exactly as the server rendered it.
  function allowed() {
    try {
      var c = navigator.connection;
      if (!c) return true;
      if (c.saveData) return false;
      return !/(^|-)(2g|slow-2g)$/.test(c.effectiveType || '');
    } catch (e) { return true; }
  }

  function idle(fn) {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 3000 });
    else setTimeout(fn, 800);
  }

  function getJSON(url) {
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, TIMEOUT);
    return fetch(url, ctl ? { signal: ctl.signal } : undefined)
      .then(function (res) {
        clearTimeout(timer);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      }, function (e) { clearTimeout(timer); throw e; });
  }

  function objectPath(name) {
    return BASE + '/' + encodeURIComponent(ACCOUNT + '/' + name) + '?alt=media';
  }

  // ---- the source ---------------------------------------------------------

  /*
    Every card id the account holds. Card objects are named "<cardid>__".

    The page token loop matters more than it looks. A missing recipe is read as
    a deletion by freshenIndex, so a listing that stops at the first page would
    quietly retire every recipe past it the day the library passes a thousand.
    scripts/source_state.py paginates for the same reason; this is the same
    loop.
  */
  function listCardIds() {
    var prefix = encodeURIComponent(ACCOUNT + '/cards/');
    var ids = [], pages = 0;
    function page(token) {
      var url = BASE + '?prefix=' + prefix + '&maxResults=1000' +
                (token ? '&pageToken=' + encodeURIComponent(token) : '');
      return getJSON(url).then(function (d) {
        (d.items || []).forEach(function (i) {
          var n = String(i.name || '').split('/').pop().replace(/_+$/, '');
          if (n) ids.push(n);
        });
        // Guard on PAGES, not ids: a response with an empty items array but a
        // nextPageToken would never advance an id count, and the loop would
        // run until the tab died.
        if (d.nextPageToken && ++pages < 40) return page(d.nextPageToken);
        return ids;
      });
    }
    return page(null);
  }

  function fetchCard(cardid) {
    return getJSON(objectPath('cards/' + cardid + '__'));
  }

  // The stored image is an imgur URL that serves a placeholder; the vendor's
  // proxy serves the real photograph. Anything already on our own CDN is left
  // alone.
  function liveImage(url) {
    if (!url) return '';
    if (url.indexOf('cdn.shopify.com') !== -1) return url;
    var m = /i\.imgur\.com\/([A-Za-z0-9._-]+)/.exec(url);
    return m ? IMAGE_PROXY + encodeURIComponent(m[1]) : url;
  }

  // The lookup the exporter publishes: handles by card id, plus the ids of
  // videos YouTube has made private.
  var lookupPromise = null;
  function lookup() {
    if (!lookupPromise) {
      // No default on purpose. The asset URL carries Shopify's cache
      // fingerprint, which this script cannot construct, and getJSON(undefined)
      // would fetch the current page, get HTML, and fail into an empty lookup -
      // silently dropping every similar-recipe link and un-hiding the six dead
      // video embeds. Better to do nothing at all.
      if (!cfg.handlesUrl) return Promise.resolve({ live: {}, taken: {}, novideo: [] });
      lookupPromise = getJSON(cfg.handlesUrl)
        .then(function (d) {
          // "live" is the card ids that have a published page. A recipe Tammy
          // deletes keeps its handle on record but its page is set to DRAFT
          // and 404s, so "has a handle" and "has a page" are different
          // questions, and only the second decides whether a card may link to
          // it. "taken" is every handle ever assigned, live or not, which is
          // what a new recipe's handle has to be de-duplicated against.
          var live = d.h || {};
          var taken = {};
          for (var cid in live) taken[live[cid]] = 1;
          (d.retired || []).forEach(function (h) { taken[h] = 1; });
          lookupValue = { live: live, taken: taken, novideo: d.novideo || [] };
          return lookupValue;
        })
        .catch(function () { return { live: {}, taken: {}, novideo: [] }; });
    }
    return lookupPromise;
  }

  /*
    The spelling map the exporter publishes.

    Categories are typed free-hand, so one concept arrives under several
    spellings - "Dessert" beside "Desserts", "Slow cooker" beside "Crockpot".
    scripts/export_index_asset.py decides one spelling per concept with the
    whole library in hand and publishes the merge map it used; without it the
    live read re-splits them and Desserts fell from 164 recipes to 155.

    It used to be passed in by the caller, which only worked when the recipe
    index happened to start the read first - the mega menu and the footer pass
    nothing. The layer loads it itself so the answer cannot depend on which
    section got there first.
  */
  var taxonomyPromise = null;
  function taxonomy() {
    if (!taxonomyPromise) {
      taxonomyPromise = cfg.taxonomyUrl
        ? getJSON(cfg.taxonomyUrl).then(function (m) { return m || {}; },
                                        function () { return {}; })
        : Promise.resolve({});
    }
    return taxonomyPromise;
  }

  function videoOkFor(r, lk) {
    if (!r.youtube_id) return undefined;
    return lk.novideo.indexOf(r.youtube_id) === -1;
  }

  // scripts/import_all.py's rule, reproduced so a link built here matches the
  // one the sync will create: slugify the title, and if another recipe already
  // owns that handle, append the cookbook number.
  function handleFor(r, lk) {
    var h = r.handle;
    if (lk.taken[h] && lk.live[r.cardid] !== h) {
      return h + '-' + (r.cookbook_number || r.cardid.slice(-4));
    }
    return h;
  }

  // ---- the index grid -----------------------------------------------------

  // scripts/export_index_asset.py's compact row: the ten keys the grid reads.
  // Built here for a recipe the exporter has not seen yet.
  function indexRow(r, handle, existingLabels, aliases) {
    var ing = '';
    r.ingredients.forEach(function (g) {
      g.items.forEach(function (i) { ing += (ing ? ' ' : '') + i; });
    });
    ing = ing.replace(/<[^>]+>/g, '').toLowerCase().slice(0, 240);
    return {
      h: handle,
      t: r.display_title,
      i: liveImage(r.image),
      p: r.prep_minutes,
      c: r.cook_minutes,
      cat: fold(r.categories_raw, existingLabels.cat, aliases.cat).join('|'),
      m: fold(r.methods_raw, existingLabels.m, aliases.m).join('|'),
      cu: fold(r.cuisine_raw, existingLabels.cu, aliases.cu)[0] || '',
      n: (r.nutrition || {}).calories || '',
      ing: ing
    };
  }

  // The exporter decides one spelling per concept with the whole library in
  // hand (canonical_spellings), which a browser holding one new recipe cannot
  // redo. It does not need to: every label already in the grid is a decision
  // that has been made, so a new recipe's label is folded onto the existing
  // one it matches. That is the same outcome, reached from the other end.
  var EDGE_JUNK = /^[\s`'"\u201c\u201d\u2018\u2019.,;:!\/\\|*#+\-\u2013\u2014_]+|[\s`'"\u201c\u201d\u2018\u2019.,;:!\/\\|*#+\-\u2013\u2014_]+$/g;
  function tidy(v) {
    return String(v == null ? '' : v).replace(/\|/g, ' ').replace(/\s+/g, ' ').trim().replace(EDGE_JUNK, '').trim();
  }
  function fold(values, existing, aliasMap) {
    var out = [], seen = {};
    (values || []).forEach(function (v) {
      var t = tidy(v);
      if (!t) return;
      for (var raw in (aliasMap || {})) {
        if (raw.toLowerCase() === t.toLowerCase()) { t = aliasMap[raw]; break; }
      }
      var key = t.toLowerCase();
      if (existing && existing[key]) t = existing[key];
      if (!seen[t.toLowerCase()]) { seen[t.toLowerCase()] = 1; out.push(t); }
    });
    return out;
  }

  function labelsFrom(data) {
    var cols = { cat: {}, m: {}, cu: {} };
    data.forEach(function (r) {
      ['cat', 'm', 'cu'].forEach(function (k) {
        String(r[k] || '').split('|').forEach(function (v) {
          if (v) cols[k][v.toLowerCase()] = v;
        });
      });
    });
    return cols;
  }

  /*
    Bring the grid into line with the source.

    Additions and removals are the whole job here and they cost one request:
    the card listing names every recipe the account holds, and anything in it
    that the grid has no handle for is new. Edits to an already-listed recipe
    are deliberately NOT chased - that would mean fetching all 708 cards on
    every visit to find the handful that changed, which is a lot of someone's
    data to spend on a title that has been retyped. An edit shows on the recipe
    page itself, which costs one request, and in the grid after the next sync.
  */
  /*
    One top-up per page, shared by every section that shows recipe numbers.

    The index grid, the mega menu's category counts, the home page tiles and
    the footer column each read cvc-recipes.json and count it themselves. If
    each also asked the source what had changed, one page view would fetch the
    93KB listing four times. They share this promise instead, so it is fetched
    once and every count on the page agrees with every other.

    The result is also kept for the rest of the browsing session, so moving
    between pages does not re-fetch it. Ten minutes is short enough that a
    recipe Tammy adds mid-visit still turns up on the next page, and long
    enough that ordinary browsing costs one request.
  */
  var CACHE_KEY = 'cvc-live-ids';
  var CACHE_MS = 10 * 60 * 1000;
  var idsPromise = null;

  function cachedCardIds() {
    if (idsPromise) return idsPromise;
    try {
      var hit = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (hit && (Date.now() - hit.at) < CACHE_MS && Array.isArray(hit.ids)) {
        idsPromise = Promise.resolve(hit.ids);
        return idsPromise;
      }
    } catch (e) { /* private browsing, or storage is full */ }
    idsPromise = listCardIds().then(function (ids) {
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), ids: ids })); }
      catch (e) { /* not being able to remember it is not a failure */ }
      return ids;
    });
    return idsPromise;
  }

  // Every section on the page gets the same topped-up array, computed once.
  //
  // Each section fetches and parses cvc-recipes.json for itself, so they hold
  // DIFFERENT arrays of the same rows. Memoising one promise meant only the
  // first caller's array was ever topped up and everyone else got their own
  // untouched copy back believing it was fresh - so the footer and the mega
  // menu went on showing the old counts.
  //
  // The expensive part is the source listing, and cachedCardIds already shares
  // that. What is memoised here is therefore the ANSWER - which cards are new
  // or gone - and it is then applied to each caller's own array.
  /*
    The live recipe data, for any section that shows recipes.

    Every surface on the site - the index grid, the mega menu counts, the home
    tiles, the footer column, search - fetches the same cvc-recipes.json for
    itself, and that file only changes when the sync runs. This is what makes
    all of them show what Recipes Generator says instead.

    It is deliberately the SAME work for every caller: the library is read once
    per session, kept in the session store, and then applied to each caller's
    own copy of the rows. So a page with four recipe sections on it costs one
    read, not four, and the second page costs none.

    Only the recipe index waits for this. Everywhere else paints from the file
    first and is updated when this resolves, because a category count being a
    moment behind is not worth holding a page for.
  */
  var freshPromise = null;
  function withFreshData(rows, aliases) {
    if (!Array.isArray(rows) || !rows.length) return Promise.resolve(rows);
    // Whatever has already been read this session costs nothing to apply.
    applyCachedRows(rows);
    if (!freshPromise) {
      // One read of the library per page, shared by every section on it. The
      // first caller's array is the one refreshAll walks; the rest are brought
      // into line from the session store afterwards.
      freshPromise = refreshAll({ data: rows, aliases: aliases || {} })
        .then(function () { return true; }, function () { return false; });
    }
    return freshPromise.then(function () {
      applyCachedRows(rows);
      return rows;
    }, function () { return rows; });
  }

  // Apply whatever refreshAll has already read, from the session's own store.
  function applyCachedRows(rows) {
    var cache = cachedRows();
    if (!cache) return 0;
    var lkp = lookupSync();
    if (!lkp) return 0;
    var byHandle = {};
    rows.forEach(function (r) { byHandle[r.h] = r; });
    var n = 0;
    Object.keys(cache).forEach(function (cid) {
      var row = byHandle[lkp.live[cid]];
      if (!row) return;
      var c = cache[cid];
      COMPACT_KEYS.forEach(function (k) { if (c[k] !== undefined) row[k] = c[k]; });
      n++;
    });
    return n;
  }

  // lookup() is a promise; this is the already-resolved value when there is
  // one, so a caller that must act synchronously can still use it.
  var lookupValue = null;
  function lookupSync() { return lookupValue; }

  function freshenIndex(opts) {
    return topUp(opts.data, opts.aliases || {});
  }

  function topUp(data, aliases) {
    if (!allowed() || !Array.isArray(data) || !data.length) return Promise.resolve(null);

    return Promise.all([cachedCardIds(), lookup()]).then(function (both) {
      var ids = both[0], lk = both[1];
      if (!ids.length) return null;

      // WITHOUT THE LOOKUP, DO NOTHING.
      //
      // Every judgement below - is this card new, has it been retired, which
      // handle does it link to - is made against lk.live. When cvc-handles.json
      // cannot be read, lookup() returns an empty map, and then EVERY card id
      // looks new: the grid gained 25 duplicate recipes and the count went from
      // 708 to 733. An empty lookup is missing information, not the answer that
      // nothing is known, so the top-up is skipped entirely.
      if (!lk || !Object.keys(lk.live).length) return null;

      // A listing that comes back short is far more likely to be a truncated
      // or partial response than Tammy deleting her library, and acting on it
      // would splice hundreds of recipes out of the grid. scripts/source_state
      // .py refuses below a fixed floor for the same reason; here the floor is
      // taken from what the sync last published, which needs no maintenance.
      if (ids.length < data.length * 0.9) return null;

      var known = {};
      data.forEach(function (r) { known[r.h] = 1; });
      var handleByCard = lk.live;

      // Removed: a card id the source no longer lists, whose handle is in the
      // grid. The sync sets these to DRAFT; until it runs they would link to
      // a page that is about to disappear.
      var live = {};
      ids.forEach(function (id) { live[id] = 1; });
      var dropped = {};
      for (var cid in handleByCard) {
        if (!live[cid] && known[handleByCard[cid]]) dropped[handleByCard[cid]] = 1;
      }

      // New: a card id with no handle on record.
      // No page yet: either the sync has never seen this card, or it saw it,
      // retired it, and the source has brought it back.
      var fresh = ids.filter(function (id) { return !lk.live[id]; });

      if (!fresh.length && !Object.keys(dropped).length) return null;

      var labels = labelsFrom(data);
      // A burst of new recipes is a handful, not hundreds; cap it anyway so a
      // surprise can never turn into 700 requests from one visitor.
      var take = fresh.slice(0, 25);
      return Promise.all(take.map(function (id) {
        return fetchCard(id).then(function (card) {
          var r = T.transform(card, id);
          return indexRow(r, handleFor(r, lk), labels, aliases);
        }).catch(function () { return null; });
      })).then(function (rows) {
        // Never add a handle that is already on the grid. The lookup guard
        // above should make this impossible; this is what stops a future
        // change to that reasoning turning into duplicate cards on the page.
        var added = rows.filter(Boolean).filter(function (row) { return !known[row.h]; });
        var removedCount = 0, removedHandles = [];
        for (var i = data.length - 1; i >= 0; i--) {
          if (dropped[data[i].h]) { removedHandles.push(data[i].h); data.splice(i, 1); removedCount++; }
        }
        // Newest first, which is the order the exporter writes and the order
        // the grid is read in.
        added.reverse().forEach(function (row) { data.unshift(row); });
        if (!added.length && !removedCount) return null;
        return { added: added.length, removed: removedCount,
                 skipped: fresh.length - take.length,
                 rows: data, addedRows: added, removedHandles: removedHandles };
      });
    }).catch(function () { return null; });
  }

  /*
    Catch EDITS to the recipes currently on screen.

    Additions and removals cost one request, so the grid gets those for free.
    Edits are different: the source exposes no bulk change signal - the listing
    carries names only, and a checksum needs one request per card - so knowing
    that any of 708 recipes had been retyped would mean 708 requests. The old
    Weebly index does exactly that, which is why it fills in so slowly.

    So only what the visitor can actually see is checked, a few at a time. The
    cards are served with a strong ETag, so on a second visit the browser
    revalidates and gets 304s with no body, and a card once checked is not
    checked again this session.
  */
  var checked = {};
  // The most cards one page will ever ask the source about. A screenful is 12
  // and a few presses of Show more is reasonable; walking the whole library
  // one card at a time is not.
  var MAX_CARD_READS = 60;
  function freshenVisible(opts) {
    var data = opts.data;
    var handles = opts.handles || [];
    var onChange = opts.onChange || function () {};
    if (!allowed() || !handles.length) return Promise.resolve(0);

    return lookup().then(function (lk) {
      var cardByHandle = {};
      for (var cid in lk.live) cardByHandle[lk.live[cid]] = cid;
      var byHandle = {};
      data.forEach(function (r) { byHandle[r.h] = r; });

      var jobs = [];
      handles.forEach(function (h) {
        var cid = cardByHandle[h];
        if (cid && !checked[cid] && byHandle[h]) jobs.push({ cid: cid, row: byHandle[h] });
      });
      // One request per card is fine for a screenful and not fine for a
      // library. Nothing capped the count before - only the concurrency - so
      // enough presses of Show more would have walked the whole 708.
      if (jobs.length > MAX_CARD_READS) jobs = jobs.slice(0, MAX_CARD_READS);
      if (!jobs.length) return 0;

      var labels = labelsFrom(data);
      var changed = 0, i = 0;
      var changedHandles = [];
      // Six at a time. Enough to finish a screenful quickly, few enough that
      // it never competes with the images the visitor is waiting for.
      function worker() {
        if (i >= jobs.length) return Promise.resolve();
        var job = jobs[i++];
        return fetchCard(job.cid).then(function (card) {
          // Marked only on success. Marking before the request meant a card
          // whose fetch failed was never asked about again for the rest of the
          // session, so a transient blip left it stale until the next reload.
          checked[job.cid] = 1;
          var r = T.transform(card, job.cid);
          var next = indexRow(r, job.row.h, labels, opts.aliases || {});
          // The handle stays as the server assigned it: a live rename must not
          // silently repoint a card at a URL that does not exist yet.
          var keys = ['t', 'i', 'p', 'c', 'cat', 'm', 'cu', 'n', 'ing'];
          var differs = false;
          keys.forEach(function (k) {
            // The image is re-hosted by the sync, so ours and the source's are
            // different URLs for the same photograph - see contentOf.
            if (k === 'i') return;
            if (String(job.row[k]) !== String(next[k])) differs = true;
          });
          if (differs) {
            keys.forEach(function (k) { if (k !== 'i') job.row[k] = next[k]; });
            changedHandles.push(job.row.h);
            changed++;
          }
        }).catch(function () { /* one unreachable card is not a failure */ })
          .then(worker);
      }
      // Six lanes for a background top-up; the caller can ask for more when the
      // reader is waiting on the result, so a screenful finishes in one round
      // trip rather than two. Measured: 40 concurrent reads from this source
      // complete in ~2s with no rate limiting, so a dozen is comfortable.
      var lanes = [];
      var width = opts.lanes || 6;
      for (var w = 0; w < width; w++) lanes.push(worker());
      return Promise.all(lanes).then(function () {
        // Hand back WHICH rows moved, not just how many. Repainting the whole
        // grid to correct one cook time tears down every card and reloads
        // every photograph, which reads as the page flickering - the caller
        // uses this to touch only the cards that actually changed.
        if (changed) onChange(changed, changedHandles);
        return changed;
      });
    }).catch(function () { return 0; });
  }

  /*
    Bring the WHOLE library up to date, in the background.

    Checking only the cards on screen is enough to stop a visible number being
    wrong, but it cannot answer a question about a recipe that is not on screen.
    Tammy files Pineapple Salad under Southern Sides; the category filter still
    does not offer it, because nothing ever asked that card. The counts on the
    menu and the tiles have the same hole.

    The vendor's own index solves this by reading all 708 cards on every page
    load, which is why it fills in so slowly. This does the same reading but
    gets out of the reader's way:

      - it starts only after the grid has been painted, so nothing waits on it
      - six at a time, so it never competes with the photographs on screen
      - the derived rows are kept for the rest of the session, so moving
        between pages does not read them again
      - it does not run at all on Data Saver or a slow connection
      - progress is reported as it goes, so the filters and counts fill in
        rather than jumping at the end

    On a second visit almost all of it is answered from the browser cache with
    304s carrying no body, because the cards are served with a strong ETag.
  */
  var ALL_KEY = 'cvc-live-rows';
  var ALL_MS = 10 * 60 * 1000;

  function cachedRows() {
    try {
      var hit = JSON.parse(sessionStorage.getItem(ALL_KEY) || 'null');
      if (hit && (Date.now() - hit.at) < ALL_MS && hit.rows) return hit.rows;
    } catch (e) { /* private browsing, or storage is full */ }
    return null;
  }

  function rememberRows(map) {
    try { sessionStorage.setItem(ALL_KEY, JSON.stringify({ at: Date.now(), rows: map })); }
    catch (e) { /* not remembering it only costs a repeat read */ }
  }

  var COMPACT_KEYS = ['t', 'p', 'c', 'cat', 'm', 'cu', 'n', 'ing'];

  //
  // Shared per page. The recipe index calls this directly because it waits for
  // the answer, while the mega menu, footer, home tiles, search and the
  // cookbook page reach it through withFreshData - and on the index page that
  // is five callers at once. Without this the library was read twice over,
  // 1418 requests instead of 709.
  var allPromise = null;
  function refreshAll(opts) {
    if (!allPromise) allPromise = refreshAllOnce(opts);
    // Whoever asks gets the same read, and their OWN copy of the rows brought
    // into line from it. Only the first caller's array is walked directly, so
    // without this the section that happened to ask second painted the stale
    // file - the grid said 72 recipes while the mega menu beside it said 73.
    function settle(n) { applyCachedRows(opts.data); return n; }
    return allPromise.then(settle, function () { return settle(0); });
  }

  function refreshAllOnce(opts) {
    var data = opts.data;
    var onProgress = opts.onProgress || function () {};
    if (!allowed() || !Array.isArray(data) || !data.length) return Promise.resolve(0);

    // Recipes ADDED or DELETED at the source first - that is a single request
    // and it decides which rows exist at all. The per-card read below then
    // brings the rows that do exist up to date. Doing it the other way round
    // would walk a list that was missing a recipe Tammy added this morning.
    return taxonomy().then(function (tax) {
    var aliases = (opts.aliases && Object.keys(opts.aliases).length) ? opts.aliases : tax;
    return topUp(data, aliases).catch(function () { return null; })
      .then(function () {
    return Promise.all([cachedCardIds(), lookup()]).then(function (both) {
      var ids = both[0], lk = both[1];
      if (!ids.length || !Object.keys(lk.live).length) return 0;

      var byHandle = {};
      data.forEach(function (r) { byHandle[r.h] = r; });
      var labels = labelsFrom(data);

      // Anything already answered this session is applied at once, for free.
      var cache = cachedRows() || {};
      var applied = 0;
      Object.keys(cache).forEach(function (cid) {
        var handle = lk.live[cid];
        var row = handle && byHandle[handle];
        if (!row) return;
        var c = cache[cid];
        COMPACT_KEYS.forEach(function (k) { if (c[k] !== undefined) row[k] = c[k]; });
        checked[cid] = 1;
        applied++;
      });
      if (applied) onProgress(applied, false, applied, applied);

      // Newest first. Card ids run in rough date order, so this asks about the
      // recipes Tammy has touched most recently before the ones she wrote in
      // 2017 - which is what shortens the wait for the change she just made,
      // even though the whole library gets read either way.
      var todo = ids.filter(function (id) { return lk.live[id] && !checked[id]; })
                    .sort(function (a, b) { return b.localeCompare(a); });
      if (!todo.length) return applied;

      var changed = 0, i = 0, sinceReport = 0;
      function worker() {
        if (i >= todo.length) return Promise.resolve();
        var cid = todo[i++];
        var row = byHandle[lk.live[cid]];
        if (!row) return worker();
        return fetchCard(cid).then(function (card) {
          checked[cid] = 1;
          var next = indexRow(T.transform(card, cid), row.h, labels, aliases);
          var moved = false;
          COMPACT_KEYS.forEach(function (k) {
            if (String(row[k]) !== String(next[k])) { row[k] = next[k]; moved = true; }
          });
          var keep = {};
          COMPACT_KEYS.forEach(function (k) { keep[k] = row[k]; });
          cache[cid] = keep;
          if (moved) changed++;
          if (++sinceReport >= 12) { sinceReport = 0; onProgress(changed, false, i, todo.length); }
        }).catch(function () { /* one unreachable card is not a failure */ })
          .then(worker);
      }
      // The storage is served over HTTP/2, so the browser multiplexes these
      // down one connection instead of queueing them six at a time. The lane
      // count was the only thing holding the read back - the original site
      // fills in about two seconds doing exactly this, and matching it is
      // simply a matter of not throttling ourselves. The grid is not drawn
      // until this finishes, so nothing on screen is competing for bandwidth.
      var lanes = [];
      for (var w = 0; w < 150; w++) lanes.push(worker());
      return Promise.all(lanes).then(function () {
        rememberRows(cache);
        onProgress(changed, true, todo.length, todo.length);
        // Every other section on the page holds its own copy of the same rows.
        // This is how they hear that the numbers have moved.
        try {
          document.dispatchEvent(new CustomEvent('cvc:recipes-refreshed', { detail: { changed: changed } }));
        } catch (e) { /* older browser: the next page load will pick it up */ }
        return changed;
      });
    }).catch(function () { return 0; });
      });
    });
  }

  // ---- a rendered recipe page ---------------------------------------------

  var CTX = {
    // Mirrors the recipe section's own settings, so a redraw cannot put the
    // theme defaults back over a name or a disclosure Tammy has changed.
    authorName: cfg.authorName || undefined,
    affiliateNote: cfg.affiliateNote || undefined,
    rootUrl: cfg.rootUrl || '/',
    cookbookUrl: cfg.cookbookUrl || '/collections/cookbooks',
    shareUrl: cfg.shopUrl ? (cfg.shopUrl + location.pathname) : ''
  };

  /*
    Has anything that a reader would notice actually changed?

    Both sides go through the browser's own parser before being compared, for
    two reasons. The page's markup has already been through it once, so it
    holds a decoded "\u2019" where our freshly rendered string still holds the
    "&rsquo;" the template wrote - comparing the two as text reported a
    difference on every recipe that had an apostrophe in it, and redrew pages
    that had not changed. Parsing both puts them in the same form. DOMParser
    is used rather than a detached div because a detached div still starts
    downloading every image in the markup it is given, which was costing a
    request per page view for a comparison that discards the URLs anyway.

    Image URLs are deliberately excluded. Until the sync re-hosts a photograph
    we serve it through the vendor's proxy and the server serves it from our
    CDN, so the two URLs always differ for the same picture. A genuinely
    changed photograph is picked up by the sync.
  */
  var domParser = typeof DOMParser === 'function' ? new DOMParser() : null;
  function contentOf(html) {
    var out = html;
    if (domParser) {
      try {
        var doc = domParser.parseFromString('<body>' + html + '</body>', 'text/html');
        var el = doc.body.firstElementChild;
        if (el) {
          var media = el.querySelectorAll('img, iframe');
          for (var i = 0; i < media.length; i++) media[i].setAttribute('src', '');
          out = el.outerHTML;
        }
      } catch (e) { /* fall back to the raw string */ }
    }
    return out
      .replace(/\ssrc="[^"]*"/g, ' src=""')
      // The Pinterest share link carries the hero image URL in its "media"
      // parameter, so it differs for the same photograph for exactly the same
      // reason the <img> does. Excluded on the same grounds.
      .replace(/media=[^"&]*/g, 'media=')
      .replace(/>[\s]+</g, '><')
      .replace(/[\s]+/g, ' ')
      .trim();
  }

  /*
    Harvest the similar-recipe block the server already rendered.

    Two things come out of it, and both have to, because neither can be worked
    out from the source card:

      the thumbnail - ours are re-hosted on Shopify's CDN and the card only
        knows the vendor URL, so rebuilding from the card alone would swap
        every thumbnail for a slower one.
      the label - the importer deliberately labels each link with the target
        recipe's CURRENT name rather than the one stored on the source card,
        because a stored name goes stale when Tammy renames a recipe. One card
        still calls Banana Cream Pie "Master Cookbook Recipe #431: Banana Cream
        Pie" with an emoji in front. The page in front of us already has the
        right names, so they are read from there rather than shipped in yet
        another asset.
  */
  function similarFromDom(root, lk) {
    var images = {}, names = {}, cardByHandle = {};
    for (var cid in lk.live) cardByHandle[lk.live[cid]] = cid;
    var links = root.querySelectorAll('.cvc-similar-post');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var handle = (a.getAttribute('href') || '').split('/pages/recipes/')[1];
      var card = handle && cardByHandle[handle];
      if (!card) continue;
      var img = a.querySelector('img');
      if (img) images[card] = img.getAttribute('src');
      var label = a.querySelector('.cvc-similar-post-name');
      // textContent, not innerHTML. The name is stored as plain text and the
      // template prints it raw into the span but escaped into the alt, so
      // harvesting the encoded form fed "Chip Beef &amp; Gravy" back in and
      // the alt came out double-escaped as "&amp;amp;" - which read as a
      // change on every recipe whose similar list contained an ampersand.
      if (label) names[card] = label.textContent;
    }
    return { images: images, names: names };
  }

  function buildFields(r, lk, opts) {
    opts = opts || {};
    return R.fieldValues(r, {
      // The caller passes the image already on the page where there is one;
      // only a recipe with no page yet falls back to the vendor's proxy.
      heroImage: opts.heroImage !== undefined ? opts.heroImage : liveImage(r.image),
      videoOk: videoOkFor(r, lk),
      handlesByCardId: lk.live,
      // Empty on a page that has no similar block to read - a recipe the sync
      // has not imported yet. Its similar links then carry the name the source
      // card stores, which is right for all but a handful of entries and only
      // until the next sync.
      namesByCardId: opts.similarNames || {},
      imagesByCardId: opts.similarImages || {}
    });
  }

  // The cook-mode toggle is wired by an inline script that runs once at page
  // load. Replacing the markup orphans that listener, so it is re-wired here.
  // Kept in step with the block at the end of sections/main-recipe.liquid.
  function wireCookMode(root) {
    var btn = root.querySelector('.cvc-switch-toggle');
    if (!btn) return;
    if (!('wakeLock' in navigator)) {
      var box = btn.closest('.cvc-cook-mode');
      if (box) box.hidden = true;
      return;
    }
    var lock = null;
    btn.addEventListener('click', function () {
      if (lock) {
        lock.release().then(function () { lock = null; btn.setAttribute('aria-pressed', 'false'); },
                            function () {});
      } else {
        navigator.wakeLock.request('screen').then(function (l) {
          lock = l; btn.setAttribute('aria-pressed', 'true');
        }, function () {});
      }
    });
  }

  // The visible page and the structured data have to agree, or the page tells
  // a crawler one thing and a reader another.
  function updateRecipeLd(f) {
    var scripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < scripts.length; i++) {
      if ((scripts[i].textContent || '').indexOf('"Recipe"') === -1) continue;
      try { scripts[i].textContent = R.renderRecipeLd(f, CTX); } catch (e) { /* leave it */ }
      return;
    }
  }

  // The title and the social tags are built in the layout, outside the section,
  // so a redraw of the body would otherwise leave them describing the old text.
  // The layout builds the tab title from the recipe's name with the internal
  // "- Master Cookbook Recipe # 431" tail cut off. Writing f.title back put
  // that tail into the tab, the og:title and the Twitter card - so a redraw
  // renamed the page to its internal label. Same cut as theme.liquid.
  function shortTitle(t) {
    return String(t == null ? '' : t)
      .replace(/<[^>]+>/g, '')
      .replace(/\s*[,\u2013\u2014-]\s*master\s+cook\w*(?:\s+recip\w*)?\s*#?\s*\d*\s*$/i, '')
      .trim();
  }

  function updateHead(f) {
    try {
      var name = shortTitle(f.title);
      if (name && document.title) {
        // Keep everything the layout appended after the recipe's name - the
        // word "Recipe" and the shop name both live in that tail.
        var sep = document.title.indexOf(' \u2013 ');
        var tail = sep > 0 ? document.title.slice(sep) : '';
        var lead = sep > 0 ? document.title.slice(0, sep) : document.title;
        // "Banana Cream Pie Recipe" -> keep the " Recipe".
        var suffix = /\sRecipe$/.test(lead) ? ' Recipe' : '';
        document.title = name + suffix + tail;
      }
      var desc = String(f.summary || '').replace(/<[^>]+>/g, '').trim().slice(0, 300);
      [['meta[name="description"]', desc],
       ['meta[property="og:title"]', name + ' Recipe'],
       ['meta[property="og:description"]', desc],
       ['meta[name="twitter:title"]', name + ' Recipe'],
       ['meta[name="twitter:description"]', desc]].forEach(function (pair) {
        var el = document.querySelector(pair[0]);
        if (el && pair[1]) el.setAttribute('content', pair[1]);
      });
    } catch (e) { /* the body is what matters; never fail over a meta tag */ }
  }

  /*
    Redraw an already-rendered recipe page if its source has moved on.

    One request, and usually a free one: the card carries a strong ETag and
    Cache-Control that makes the browser revalidate, so an unchanged recipe
    costs a 304 with no body. Nothing is touched unless the content genuinely
    differs, and if anything at all goes wrong the server's render is left
    exactly as it was.
  */
  function freshenDetail() {
    if (!allowed()) return Promise.resolve(null);
    var root = document.querySelector('.cvc-recipe');
    if (!root) return Promise.resolve(null);
    var cardid = root.getAttribute('data-legacy-cardid');
    if (!cardid) return Promise.resolve(null);

    return Promise.all([fetchCard(cardid), lookup()]).then(function (both) {
      var r = T.transform(both[0], cardid);
      var lk = both[1];
      var sim = similarFromDom(root, lk);
      // Keep the hero the server rendered. Ours is re-hosted on Shopify's CDN;
      // the only image the browser can name is the vendor's proxy, which is
      // the same photograph served more slowly - and it would follow the
      // redraw into the Pinterest share link and the JSON-LD image as well. A
      // genuinely changed photograph arrives with the next sync.
      var heroEl = root.querySelector('.cvc-image img');
      var f = buildFields(r, lk, {
        similarImages: sim.images,
        similarNames: sim.names,
        heroImage: heroEl ? heroEl.getAttribute('src') : undefined
      });
      var fresh = R.renderRecipe(f, CTX);
      if (contentOf(fresh) === contentOf(root.outerHTML)) return null;

      var holder = document.createElement('div');
      holder.innerHTML = fresh;
      var next = holder.firstElementChild;
      if (!next || !next.classList.contains('cvc-recipe')) return null;
      root.replaceWith(next);
      wireCookMode(next);
      // The batch-size control went out with the old node. Tell whoever is
      // listening so it can be wired up again on the new one, with the
      // reader's chosen batch size put back over the fresh amounts.
      document.dispatchEvent(new CustomEvent('cvc:recipe-redrawn'));
      updateRecipeLd(f);
      updateHead(f);
      return { updated: true, cardid: cardid };
    }).catch(function () { return null; });
  }

  /*
    Draw a recipe that Shopify has no page for yet.

    A recipe Tammy added since the last sync has no metaobject, so its URL
    404s. This renders it into the 404 page from the source, at the URL it will
    keep once the sync imports it - so a link shared today still works
    tomorrow. The page is marked noindex while it is drawn this way: it is a
    404 as far as the server is concerned, and we do not want that indexed.
  */
  function renderUnknownRecipe(mount, handle) {
    if (!mount || !handle) return Promise.resolve(null);
    return Promise.all([cachedCardIds(), lookup()]).then(function (both) {
      var ids = both[0], lk = both[1];
      var unknown = ids.filter(function (id) { return !lk.live[id]; });
      if (!unknown.length) return null;
      // Fetch only the ones with no page yet, and stop at the first whose
      // handle matches the URL.
      return unknown.slice(0, 25).reduce(function (chain, id) {
        return chain.then(function (found) {
          if (found) return found;
          return fetchCard(id).then(function (card) {
            var r = T.transform(card, id);
            return handleFor(r, lk) === handle ? { r: r, lk: lk } : null;
          }).catch(function () { return null; });
        });
      }, Promise.resolve(null));
    }).then(function (hit) {
      if (!hit) return null;
      var f = buildFields(hit.r, hit.lk, {});
      mount.innerHTML = R.renderRecipe(f, CTX);
      wireCookMode(mount);
      // Same signal the detail redraw sends. A recipe drawn here arrives long
      // after the page loaded, so whatever wires up the batch-size dropdown has
      // already run once and found nothing; without this it never runs again.
      document.dispatchEvent(new CustomEvent('cvc:recipe-redrawn'));
      var ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.textContent = R.renderRecipeLd(f, CTX);
      mount.appendChild(ld);
      updateHead(f);
      var robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex,follow');
      return { rendered: true, cardid: hit.r.cardid };
    }).catch(function () { return null; });
  }

  window.CVCLive = {
    refreshAll: refreshAll,
    freshenVisible: freshenVisible,
    withFreshData: withFreshData,
    contentOf: contentOf,        // exported so the browser tests can use the real one
    freshenDetail: freshenDetail,
    renderUnknownRecipe: renderUnknownRecipe,
    freshenIndex: freshenIndex,
    indexRow: indexRow,
    allowed: allowed,
    listCardIds: listCardIds,
    fetchCard: fetchCard,
    liveImage: liveImage,
    lookup: lookup,
    videoOkFor: videoOkFor,
    handleFor: handleFor,
    getJSON: getJSON,
    idle: idle
  };
})();
